import { defineStore } from 'pinia';
import {
  createBaseFilterState,
  createFilterActions,
  createFilterGetters,
  defineDefaultDistanceState,
} from './filterStoreHelpers';
import sortOptions from '@/constants/sortOptions';
import searchJobsRules from '@/constants/searchJobsRules';
import { isAbortError } from '@/utilities/isAbortError';
import useAcvDatadog from '@/composables/useAcvDatadog';

const { addRumError, addRumAction } = useAcvDatadog();

const filterAPI = 'api/transport-requests/available';
const options = {
  sort: sortOptions.recentlyUpdated,
  activeSavedSearch: null,
  isSavedSearchSideBarOpen: false,
  isCreateFormOpen: false,
  isQuickSave: false,
  savedSearches: [],
  errors: [],
  loading: false,
  createSavedSearchName: '',
  savedSearchEditID: null,
  snackBarMessage: null,
};

export const useSavedSearchStore = defineStore('savedSearchStore', {
  state: () => createBaseFilterState(options),
  actions: {
    ...createFilterActions(filterAPI, options, false),
    /**
     * Fetches all saved searches for the current carrier from the API.
     * Populates `this.savedSearches` with the response data.
     * @returns {Promise<void>}
     */
    async getSavedSearchesForCarrier() {
      this.loading = true;
      this.errors = [];
      this.savedSearches = [];
      const { $api } = useNuxtApp();

      try {
        const response = await $api('/api/v1/saved-search');

        if (response?.data?.length > 0) {
          this.savedSearches = response.data;
        }
      } catch (error) {
        this.errors.push(error);

        if (isAbortError(error)) {
          addRumAction(`Get saved searches: ${error.cause?.message || error.message}`);
        } else {
          addRumError(new Error('Failed to fetch saved searches'), {
            error_message: error.message,
            error_status: error.status || error.statusCode,
            error,
            loading: this.loading,
            search_count: this.savedSearches.length,
            active_filters: this.appliedFilters,
          });
        }
      } finally {
        this.loading = false;
      }
    },
    /**
     * Saves or updates a search with the current filter criteria.
     * If `savedSearchEditID` is set, the existing search is updated; otherwise a new one is created.
     * Refreshes the saved searches list and displays a snackbar message on success.
     * @returns {Promise<string|undefined>} The saved search ID on success, or undefined on failure
     */
    async saveSearch() {
      this.loading = true;
      this.errors = [];

      const body = {
        id: this.savedSearchEditID,
        searchName: this.createSavedSearchName,
        searchCriteria: {
          location: {
            pickup: {
              distances: this.appliedPickupDistanceFilters,
              states: this.appliedPickupStateFilters,
            },
            delivery: {
              distances: this.appliedDeliveryDistanceFilters,
              states: this.appliedDeliveryStateFilters,
            },
          },
          sort: `${this.sort.value},${this.sort.order}`,
          jobTypes: this.appliedJobTypeFilters,
        },
      };

      try {
        const { $api } = useNuxtApp();
        const response = await $api('api/v1/saved-search/', {
          method: 'POST',
          body,
        });

        if (response.data?.id) {
          await this.getSavedSearchesForCarrier();

          if (this.savedSearchEditID) {
            this.showSnackBarMessage('Your saved search has been updated.');
            this.savedSearchEditID = null;
          } else {
            this.showSnackBarMessage(`Your search has been saved as ${response.data?.searchName}`);
          }

          const { trackEvent, eventNames } = useAcvAnalytics();
          trackEvent(eventNames.SAVED_SEARCH_CREATED, {
            saved_search_id: response.data?.id,
            ...body,
          });

          return response.data?.id;
        }
      } catch (error) {
        this.errors = this.errors;
      }

      this.resetFormData();
      this.loading = false;
    },

    /** Resets the saved search form data to its default values. */
    resetFormData() {
      this.createSavedSearchName = '';
      this.location = {
        pickup: {
          distances: [defineDefaultDistanceState('pickup')],
          states: [],
        },
        delivery: {
          distances: [defineDefaultDistanceState('delivery')],
          states: [],
        },
      };
      this.sort = {
        text: 'Dispatched Date',
        value: 'dateDispatched',
        order: 'desc',
      };
      this.jobTypes = [];
      this.isQuickSave = false;
      this.savedSearchEditID = null;
    },

    /**
     * Deletes a saved search by its ID.
     * Removes it from the local list and clears the active search if it matches.
     * @param {string} id - The unique identifier of the saved search to delete
     * @returns {Promise<void>}
     */
    async deleteSavedSearch(id) {
      try {
        const { $api } = useNuxtApp();
        this.loading = true;
        this.errors = [];

        await $api(`/api/v1/saved-search/${id}`, { method: 'DELETE' });

        this.savedSearches = this.savedSearches.filter((savedSearch) => savedSearch?.id !== id);

        this.showSnackBarMessage('Your saved search has been deleted.');

        if (this.activeSavedSearch.id === id) {
          this.activeSavedSearch = null;
        }
      } catch (error) {
        this.errors = this.errors;
      }

      this.loading = false;
    },

    /**
     * Sets the active saved search by finding it in the saved searches list.
     * @param {string} id - The unique identifier of the saved search to activate
     */
    setActiveSavedSearch(id) {
      this.activeSavedSearch = this.savedSearches.find((item) => item.id === id);
    },

    /**
     * Opens the saved search form pre-populated with a specific saved search's filters.
     * Parses the saved search's sort criteria and patches the store state.
     * @param {string} id - The unique identifier of the saved search to load into the form
     */
    toggleSavedSearchFormWithSpecificFilters(id) {
      const savedSearch = this.savedSearches.find((item) => item.id === id);

      if (!savedSearch || !savedSearch.searchCriteria?.sort) {
        addRumAction('Saved search not found or invalid:', {
          selected_saved_search_id: id,
          saved_search: savedSearch,
        });
        return;
      }

      const [value, order] = savedSearch.searchCriteria.sort.split(',');

      const sortOption = searchJobsRules.DEFAULT.SORT_BY.options.find(
        (item) => item.value === value,
      );

      this.$patch({
        createSavedSearchName: savedSearch.searchName ?? '',
        location: savedSearch.searchCriteria.location ?? null,
        jobTypes: savedSearch.searchCriteria.jobTypes ?? [],
        sort: {
          order,
          value,
          text: sortOption?.text ?? '',
        },
      });

      this.activeSavedSearch = null;
      this.savedSearchEditID = id;
      this.isCreateFormOpen = true;
    },
  },
  getters: createFilterGetters(),
});
